import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, FileText, Receipt, FileCode, Download, Mail, Loader2, ExternalLink, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { useCompanyData } from '@/hooks/use-company-data';
import { useInvoices, Invoice } from '@/hooks/useInvoices';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { InvoiceViewModal } from '@/components/admin/InvoiceViewModal';
import { FiscalDocumentsUploadModal } from '@/components/admin/FiscalDocumentsUploadModal';

export function CompanyBillingDetailPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const { data: company, isLoading: companyLoading } = useCompanyData(companyId);
  const { data: invoices = [], isLoading: invoicesLoading, refetch } = useInvoices(companyId);
  
  const [viewInvoiceId, setViewInvoiceId] = useState<string | null>(null);
  const [sendingEmailId, setSendingEmailId] = useState<string | null>(null);
  const [fiscalDocsInvoice, setFiscalDocsInvoice] = useState<{id: string, companyId: string, number: string, hasDocs: boolean} | null>(null);

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(cents / 100);
  };

  const isOverdue = (dueDate: string, status: string) => {
    return status === 'pending' && new Date(dueDate) < new Date();
  };

  const getStatusBadge = (status: string, invoice?: Invoice) => {
    const statusConfig = {
      pending: { variant: 'secondary', label: 'Pendente' },
      paid: { variant: 'default', label: 'Paga' },
      overdue: { variant: 'destructive', label: 'Vencida' },
      cancelled: { variant: 'outline', label: 'Cancelada' },
    } as const;

    const config = statusConfig[status as keyof typeof statusConfig] || {
      variant: 'outline' as const,
      label: status
    };

    return (
      <div className="flex items-center gap-2">
        <Badge variant={config.variant}>{config.label}</Badge>
        {invoice?.metadata?.manual_payment && (
          <Badge variant="outline" className="text-purple-600 border-purple-600">
            Baixa Manual
          </Badge>
        )}
      </div>
    );
  };

  const handleSendEmail = async (invoiceId: string) => {
    setSendingEmailId(invoiceId);
    try {
      const { data, error } = await supabase.functions.invoke('send-invoice-email', {
        body: { invoiceId }
      });

      if (error) throw error;

      toast({
        title: "Email enviado!",
        description: `Fatura enviada com sucesso.`,
      });
    } catch (error: any) {
      toast({
        title: "Erro ao enviar email",
        description: error.message || "Não foi possível enviar o email da fatura",
        variant: "destructive"
      });
    } finally {
      setSendingEmailId(null);
    }
  };

  // Compute stats
  const stats = {
    total: invoices.length,
    totalAmount: invoices.reduce((sum, inv) => sum + inv.amount_cents, 0),
    paidAmount: invoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.amount_cents, 0),
    pendingAmount: invoices.filter(inv => inv.status === 'pending').reduce((sum, inv) => sum + inv.amount_cents, 0),
    overdueCount: invoices.filter(inv => isOverdue(inv.due_date, inv.status)).length,
  };

  // Filter invoices by type for tabs
  const boletosInvoices = invoices.filter(inv => inv.boleto_url);
  const danfeInvoices = invoices.filter(inv => inv.danfe_pdf_url);
  const xmlInvoices = invoices.filter(inv => inv.xml_file_url);

  if (companyLoading) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!company) {
    return (
      <div className="container mx-auto py-6">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold">Empresa não encontrada</h2>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/admin/billing')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => navigate('/admin/billing')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="h-6 w-6" />
            {company.name}
          </h1>
          <p className="text-muted-foreground">
            {company.doc_cnpj && `CNPJ: ${company.doc_cnpj}`}
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Faturado</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats.totalAmount)}</div>
            <p className="text-xs text-muted-foreground">{stats.total} faturas</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Pago</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{formatCurrency(stats.paidAmount)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Pendente</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{formatCurrency(stats.pendingAmount)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Em Atraso</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats.overdueCount}</div>
            <p className="text-xs text-muted-foreground">faturas vencidas</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Card>
        <Tabs defaultValue="invoices" className="w-full">
          <CardHeader>
            <TabsList>
              <TabsTrigger value="invoices" className="gap-2">
                <FileText className="h-4 w-4" />
                Faturas ({invoices.length})
              </TabsTrigger>
              <TabsTrigger value="boletos" className="gap-2">
                <Receipt className="h-4 w-4" />
                Boletos ({boletosInvoices.length})
              </TabsTrigger>
              <TabsTrigger value="danfes" className="gap-2">
                <FileText className="h-4 w-4" />
                DANFEs ({danfeInvoices.length})
              </TabsTrigger>
              <TabsTrigger value="xmls" className="gap-2">
                <FileCode className="h-4 w-4" />
                XMLs ({xmlInvoices.length})
              </TabsTrigger>
            </TabsList>
          </CardHeader>
          
          <CardContent>
            {/* Faturas Tab */}
            <TabsContent value="invoices" className="mt-0">
              {invoicesLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Número</TableHead>
                      <TableHead>Valor</TableHead>
                      <TableHead>Emissão</TableHead>
                      <TableHead>Vencimento</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          Nenhuma fatura encontrada.
                        </TableCell>
                      </TableRow>
                    ) : (
                      invoices.map((invoice) => (
                        <TableRow key={invoice.id}>
                          <TableCell className="font-mono">{invoice.invoice_number}</TableCell>
                          <TableCell className="font-medium">{formatCurrency(invoice.amount_cents)}</TableCell>
                          <TableCell>{format(new Date(invoice.issue_date), "dd/MM/yyyy", { locale: ptBR })}</TableCell>
                          <TableCell className={isOverdue(invoice.due_date, invoice.status) ? 'text-destructive font-medium' : ''}>
                            {format(new Date(invoice.due_date), "dd/MM/yyyy", { locale: ptBR })}
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(isOverdue(invoice.due_date, invoice.status) ? 'overdue' : invoice.status, invoice)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 flex-wrap">
                              <Button variant="outline" size="sm" onClick={() => setViewInvoiceId(invoice.id)}>
                                <FileText className="h-3 w-3 mr-1" />
                                Ver
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleSendEmail(invoice.id)}
                                disabled={sendingEmailId === invoice.id}
                              >
                                {sendingEmailId === invoice.id ? (
                                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                ) : (
                                  <Mail className="h-3 w-3 mr-1" />
                                )}
                                Email
                              </Button>
                              {invoice.status === 'paid' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setFiscalDocsInvoice({
                                    id: invoice.id,
                                    companyId: invoice.company_id,
                                    number: invoice.invoice_number,
                                    hasDocs: !!invoice.danfe_pdf_url
                                  })}
                                  className={invoice.danfe_pdf_url ? "border-green-600 text-green-600" : ""}
                                >
                                  <Upload className="h-3 w-3 mr-1" />
                                  {invoice.danfe_pdf_url ? 'Docs' : 'Anexar NF'}
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            {/* Boletos Tab */}
            <TabsContent value="boletos" className="mt-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fatura</TableHead>
                    <TableHead>Valor</TableHead>
                    <TableHead>Vencimento</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {boletosInvoices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        Nenhum boleto encontrado.
                      </TableCell>
                    </TableRow>
                  ) : (
                    boletosInvoices.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell className="font-mono">{invoice.invoice_number}</TableCell>
                        <TableCell className="font-medium">{formatCurrency(invoice.amount_cents)}</TableCell>
                        <TableCell>{format(new Date(invoice.due_date), "dd/MM/yyyy", { locale: ptBR })}</TableCell>
                        <TableCell>
                          {getStatusBadge(isOverdue(invoice.due_date, invoice.status) ? 'overdue' : invoice.status)}
                        </TableCell>
                        <TableCell>
                          <Button variant="outline" size="sm" asChild>
                            <a href={invoice.boleto_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="h-3 w-3 mr-1" />
                              Abrir Boleto
                            </a>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TabsContent>

            {/* DANFEs Tab */}
            <TabsContent value="danfes" className="mt-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fatura</TableHead>
                    <TableHead>Valor</TableHead>
                    <TableHead>Data Emissão</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {danfeInvoices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                        Nenhuma DANFE encontrada.
                      </TableCell>
                    </TableRow>
                  ) : (
                    danfeInvoices.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell className="font-mono">{invoice.invoice_number}</TableCell>
                        <TableCell className="font-medium">{formatCurrency(invoice.amount_cents)}</TableCell>
                        <TableCell>{format(new Date(invoice.issue_date), "dd/MM/yyyy", { locale: ptBR })}</TableCell>
                        <TableCell>
                          <Button variant="outline" size="sm" asChild>
                            <a href={invoice.danfe_pdf_url} download target="_blank" rel="noopener noreferrer">
                              <Download className="h-3 w-3 mr-1" />
                              Baixar DANFE
                            </a>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TabsContent>

            {/* XMLs Tab */}
            <TabsContent value="xmls" className="mt-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fatura</TableHead>
                    <TableHead>Valor</TableHead>
                    <TableHead>Data Emissão</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {xmlInvoices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                        Nenhum XML encontrado.
                      </TableCell>
                    </TableRow>
                  ) : (
                    xmlInvoices.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell className="font-mono">{invoice.invoice_number}</TableCell>
                        <TableCell className="font-medium">{formatCurrency(invoice.amount_cents)}</TableCell>
                        <TableCell>{format(new Date(invoice.issue_date), "dd/MM/yyyy", { locale: ptBR })}</TableCell>
                        <TableCell>
                          <Button variant="outline" size="sm" asChild>
                            <a href={invoice.xml_file_url} download target="_blank" rel="noopener noreferrer">
                              <Download className="h-3 w-3 mr-1" />
                              Baixar XML
                            </a>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TabsContent>
          </CardContent>
        </Tabs>
      </Card>

      {/* Modals */}
      <InvoiceViewModal 
        isOpen={!!viewInvoiceId}
        invoiceId={viewInvoiceId || ''} 
        onClose={() => setViewInvoiceId(null)}
        autoDownload={false}
      />
      
      {fiscalDocsInvoice && (
        <FiscalDocumentsUploadModal
          invoiceId={fiscalDocsInvoice.id}
          companyId={fiscalDocsInvoice.companyId}
          invoiceNumber={fiscalDocsInvoice.number}
          hasDocs={fiscalDocsInvoice.hasDocs}
          isOpen={!!fiscalDocsInvoice}
          onClose={() => {
            setFiscalDocsInvoice(null);
            refetch();
          }}
        />
      )}
    </div>
  );
}

export default CompanyBillingDetailPage;
